/**
 * A CHANGE REQUEST, WRITTEN INTO THE DOCUMENT ITSELF — #195.
 *
 * THE SHAPE VINNIE CHOSE, 22 August 2026. Three were drawn: a form and then the
 * document, questions beside a live preview, or writing into the document
 * itself. He chose the third, and the reason is the whole design — one
 * representation rather than two, so what is being edited IS what gets signed
 * and there is no preview that can disagree with it.
 *
 * SO THERE IS NO FORM ON THIS SCREEN. The document is generated whole from the
 * cards of a sprint, and exactly two things accept typing: the three narrative
 * paragraphs, and the assumptions table. Everything else is the board's answer.
 *
 * THE KNOWN RISK, NAMED ON THE CARD AND ANSWERED HERE. People expect a document
 * on screen to be fully editable and will try to correct an item line. The
 * screen has to say why they cannot, and the answer is the point of the card:
 * item lines come from the board, so the fix is to correct the card and the
 * document follows. That sentence is on the page rather than in a tooltip,
 * because somebody who has just tried to click into a line is not hovering.
 *
 * NOTHING IS GENERATED AND KEPT. The item lines are worked out on every render,
 * so a card corrected on the board is corrected here with no button to press.
 * That stops the moment the PDF is produced — see change-request-pdf.ts, and
 * #200's rule that nothing is sent for signature without a file.
 */
import { useState } from 'react';
import { useApp } from '../store';
import {
  STANDARD_FORM,
  changeRequestFrom,
  howMuchIsWritten,
  nothingWrittenYet,
  whyNotReady,
  type Assumption,
} from '../change-request';
import { approvedCopy, changeRequestPdf, type SignatureRow } from '../change-request-pdf';
import { displayName } from '../people';
import { thePanel } from '../signatories';
import { asDate, asTime } from '../change-request';
import { isEditable, isRejected } from '../documents';
import { can } from '../permissions';
import { decisionFromChangeRequest } from '../decisions';
import LogDecisionAction from './LogDecisionAction';
import type { Doc } from '../types';

/* DERIVE-FIRST SEED (C): an assumption whose business decision has been reached
   (anything but the default "To be confirmed") carries a settled decision worth
   promoting to the log; one still to confirm shows a muted dash, no action. */
const isSettledDecision = (d: string): boolean => {
  const t = d.trim().toLowerCase();
  return t !== '' && t !== 'to be confirmed';
};

/** A change request and a deliverables sign-off are generated from the sprint's
 *  cards; every other kind of document is a file somebody uploads. */
export const isGenerated = (doc: Doc): boolean =>
  doc.kind === 'change' || doc.kind === 'deliverables';

/** Shown on the editor under an item whose card has no plain-English summary —
 *  #228. The produced PDF omits a missing summary (clean for the customer), so
 *  the prompt to write one lives here, where the person who can is looking. */
const SUMMARY_NUDGE =
  'Add a plain-English summary on the card — it prints here for the reader who signs.';

export function ChangeRequest({
  doc,
  mayEdit,
  onProblem,
}: {
  doc: Doc;
  mayEdit: boolean;
  onProblem: (why: string) => void;
}) {
  const issues = useApp((s) => s.issues);
  const signoffs = useApp((s) => s.signoffs);
  const user = useApp((s) => s.user);
  const profiles = useApp((s) => s.profiles);
  const meta = useApp((s) => s.meta);
  const sprints = useApp((s) => s.sprints);
  const projectName = useApp((s) => s.meta.name);
  const saveDocument = useApp((s) => s.saveDocument);
  const recordAudit = useApp((s) => s.recordAudit);
  const uploadDocumentFile = useApp((s) => s.uploadDocumentFile);
  const documentFileUrl = useApp((s) => s.documentFileUrl);
  const projectLogoUrl = useApp((s) => s.projectLogoUrl);
  const role = useApp((s) => s.role);
  /* Writer-gated derive-first seed; the decisions RLS is the backstop. */
  const canSeed = can(role, 'editIssue');

  const template = STANDARD_FORM;
  const [written, setWritten] = useState(doc.written ?? nothingWrittenYet(template));
  const [making, setMaking] = useState(false);

  /*
   * ONLY THE CARDS OF THE SPRINT IT COVERS. A change request with no sprint
   * covers the whole project, which is the same choice the document list
   * already offers — and generating the entire backlog into a document
   * somebody signs is a decision, so it is the one the person made.
   */
  const covered = doc.sprint ? issues.filter((i) => i.sprint === doc.sprint) : issues;
  const issueTypes = useApp((s) => s.issueTypes);
  const live = changeRequestFrom(covered, template, issueTypes);
  /*
   * LIVE WHILE IT IS A DRAFT, FROZEN ONCE IT IS NOT — #195, 0088.
   *
   * A draft reads the board on every render, which is what lets a wrong item
   * line be fixed by correcting the card. The moment it is sent it shows the
   * snapshot taken when the PDF was produced instead — the cards as they were
   * signed, not as they stand now. A document written before the snapshot
   * existed has none and falls back to live, which is the old behaviour.
   */
  const editing = isEditable(doc);
  const request = editing ? live : (doc.scope ?? live);
  const progress = howMuchIsWritten(written, template);
  const notReady = whyNotReady(request, written, template);
  const sprintName = sprints.find((s) => s.id === doc.sprint)?.name ?? 'The whole project';

  /** Written on leaving a box rather than on every keystroke, like the note. */
  const keep = async (next: typeof written) => {
    setWritten(next);
    onProblem(await saveDocument(doc.id, { written: next }));
  };

  /*
   * ── SECTION 6, FROM WHOEVER IS ACTUALLY ON THE HOOK ──────────────────────
   *
   * Vinnie, 23 August 2026: *"Where is the record on the PDF form that it was
   * signed off?"* and *"that approved PDF should be printable to show someone
   * who approved, when and by whom."*
   *
   * TWO SOURCES, AND WHICH ONE IS RIGHT DEPENDS ON WHERE THE DOCUMENT IS. Once
   * it has been sent, the rows are FROZEN onto it — copied at that moment and
   * never following the panel again, which is what makes a signature evidence.
   * Before it is sent there are no rows yet, so the panel from Settings is who
   * WILL be asked. Printing the live panel onto a sent document would let
   * somebody change who is shown as having signed by editing a setting.
   */
  const rows = signoffs.filter((r) => r.documentId === doc.id).sort((a, b) => a.seq - b.seq);
  const signatures: SignatureRow[] =
    rows.length > 0
      ? rows.map((r) => ({
          title: r.position ?? '',
          name: r.name ?? '',
          how: !r.signedAt
            ? ''
            : r.answer === 'rejected'
              ? 'Rejected'
              : r.onBehalfBy
                ? `Approved on behalf by ${r.onBehalfBy}`
                : r.answeredVia === 'link'
                  ? 'Approved by link'
                  : 'Approved in FlightDeck',
          signedOn: r.signedAt ? asDate(r.signedAt) : '',
          signedTime: r.signedAt ? asTime(r.signedAt) : '',
        }))
      : thePanel(meta).map((p) => ({
          title: p.position,
          name: p.name,
          how: '',
          signedOn: '',
          signedTime: '',
        }));

  /*
   * FETCHED HERE AND HANDED TO THE LAYOUT, which reaches for nothing itself.
   * A logo that cannot be fetched costs the branding and not the document.
   */
  const theLogo = async () => {
    if (!meta.logoPath) return undefined;
    try {
      const address = await projectLogoUrl();
      if (!address) return undefined;
      return {
        bytes: await (await fetch(address)).arrayBuffer(),
        type: (meta.logoType === 'jpg' ? 'jpg' : 'png') as 'png' | 'jpg',
      };
    } catch {
      return undefined;
    }
  };

  const produce = async () => {
    setMaking(true);
    try {
      const bytes = await changeRequestPdf(
        {
          title: doc.title,
          board: projectName,
          sprint: sprintName,
          /* The date it was produced, stamped once here rather than read inside
             the generator, so the same document twice is the same file. */
          raisedOn: asDate(Date.now()),
          raisedBy: user?.email ? displayName(user.email, profiles) : '',
          version: String(doc.version ?? 1),
          logo: await theLogo(),
        },
        request,
        written,
        template
      );
      /*
       * THROUGH THE ORDINARY UPLOAD, not around it. That is what fingerprints
       * the file, records its size and type, and refuses to touch a document
       * that has already been sent — three things a second path would have to
       * get right again and would eventually get wrong.
       */
      /*
       * A FILENAME A CLIENT RECEIVES, so it is tidied rather than merely made
       * safe. Stripping the punctuation on its own left "Change Request  Dosely
       * 1.4.0.pdf" — the em dash gone and its two spaces still there, which
       * reads as a mistake in the one part of the document somebody sees before
       * they open it. Anything not allowed becomes a space, and runs of spaces
       * collapse to one.
       */
      const name = `${
        doc.title
          .replace(/[^\w\-.]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim() || 'Change request'
      }.pdf`;
      const file = new File([bytes as BlobPart], name, { type: 'application/pdf' });
      /*
       * THE SNAPSHOT IS TAKEN HERE, with the same `request` the PDF was built
       * from, so the document the app shows after signing is the one in the file.
       * Produce only runs on a draft, so this write is always allowed; 0088
       * freezes it the moment the document is sent.
       */
      const up = await uploadDocumentFile(doc.id, file);
      onProblem(up || (await saveDocument(doc.id, { scope: request })));
    } catch (e) {
      onProblem((e as { message?: string })?.message ?? 'The document could not be produced.');
    }
    setMaking(false);
  };

  /*
   * THE APPROVED COPY, WHICH IS A DOWNLOAD RATHER THAN A REPLACEMENT.
   *
   * The file on the document is the one people signed and the rules refuse to
   * change it once it has been sent — correctly. So the record of approval is
   * that same file with a page added, handed to whoever asked for it, and
   * nothing about the signed document moves.
   */
  const download = async () => {
    setMaking(true);
    try {
      const address = await documentFileUrl(doc.id);
      if (!address) throw new Error('The signed file could not be found.');
      const bytes = await approvedCopy(
        await (await fetch(address)).arrayBuffer(),
        {
          title: doc.title,
          board: projectName,
          sprint: sprintName,
          raisedOn: asDate(Date.now()),
          version: String(doc.version ?? 1),
          fileHash: doc.fileHash,
          /* The record page is the one somebody prints on its own to show who
             approved what, so it is branded like every other page. */
          logo: await theLogo(),
        },
        signatures
      );
      const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(doc.fileName ?? 'Change request.pdf').replace(/\.pdf$/i, '')} — approved.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      /*
       * A SIGNED DOCUMENT LEAVING AS A FILE — #305. The produced draft is
       * recorded already, because it goes through the ordinary upload. This
       * one does not: it is made in the browser and handed straight to the
       * person, and it is the copy that carries the signatures. Who took one,
       * and when, is exactly what a signed document is for.
       */
      // The reason goes somewhere — see the same call in Settings.tsx. A write
      // that fails silently is indistinguishable from one never attempted, and
      // that is how this went unnoticed on 29 August.
      void recordAudit('project', `downloaded the approved copy of “${doc.title}”`).catch((err: unknown) =>
        onProblem(
          `The copy was made, but History could not be told — ${(err as { message?: string })?.message || String(err)}`
        )
      );
    } catch (e) {
      onProblem((e as { message?: string })?.message ?? 'The approved copy could not be made.');
    }
    setMaking(false);
  };

  /* Only worth offering once somebody has actually answered — a record of
     approval with nothing on it is a blank page with a title. */
  const anyoneSigned = signatures.some((r) => r.signedOn);

  return (
    <div className="cr fd-form">
      <div className="cr-head">
        <span className="cr-covers">
          {sprintName} · {request.total} item{request.total === 1 ? '' : 's'}
        </span>
        {mayEdit && (
          <span className="cr-progress" aria-label={`${progress.done} of ${progress.of} written`}>
            {progress.done} of {progress.of} written
          </span>
        )}
      </div>

      {/* The parts a person owns. */}
      {template.narratives.map((section) => (
        <section className="fd-sec" key={section.key}>
          <h3 className="fd-sec-h">{section.heading}</h3>
          {mayEdit ? (
            <label className="stack">
              <span className="sr-only">{section.heading}</span>
              <textarea
                className="input cr-write"
                rows={3}
                placeholder={section.help}
                value={written.narratives[section.key] ?? ''}
                onChange={(e) =>
                  setWritten({
                    ...written,
                    narratives: { ...written.narratives, [section.key]: e.target.value },
                  })
                }
                onBlur={() => {
                  if ((doc.written?.narratives?.[section.key] ?? '') !== (written.narratives[section.key] ?? ''))
                    void keep(written);
                }}
              />
            </label>
          ) : (
            <p className={written.narratives[section.key]?.trim() ? 'cr-said' : 'cr-said is-empty'}>
              {written.narratives[section.key]?.trim() || 'Not written yet.'}
            </p>
          )}
        </section>
      ))}

      {/* The board's answer. */}
      <section className="fd-sec">
        <h3 className="fd-sec-h">What is being asked for</h3>
        <p className="hint cr-why">
          These lines are the cards in {sprintName}. They cannot be typed over — correct the card and
          this follows.
        </p>

        {request.sections.length === 0 && request.deferred.length === 0 && (
          <p className="cr-said is-empty">Nothing is in {sprintName} yet.</p>
        )}

        {request.sections.map((section) => (
          <section className="cr-group" key={section.heading}>
            <h5 className="cr-group-h">{section.heading}</h5>
            {section.items.map((item) => (
              <div className="cr-item" key={item.id}>
                <span className="cr-item-t">{item.title}</span>
                {/* The card's plain-English summary — what each item IS, for the
                    reader who signs. A card with none, while the document is
                    still a draft, shows a prompt HERE (on the editor only); the
                    produced PDF leaves it off, so a customer never sees it. */}
                {item.summary ? (
                  <span className="cr-item-s">{item.summary}</span>
                ) : item.summary === '' && editing ? (
                  <span className="cr-item-gap">{SUMMARY_NUDGE}</span>
                ) : null}
                <span className="cr-item-d">{item.detail}</span>
              </div>
            ))}
          </section>
        ))}

        {request.deferred.length > 0 && (
          <section className="cr-group">
            <h5 className="cr-group-h">{template.deferredHeading}</h5>
            {request.deferred.map((item) => (
              <div className="cr-item" key={item.id}>
                <span className="cr-item-t">{item.title}</span>
                {item.summary ? (
                  <span className="cr-item-s">{item.summary}</span>
                ) : item.summary === '' && editing ? (
                  <span className="cr-item-gap">{SUMMARY_NUDGE}</span>
                ) : null}
                <span className="cr-item-d">{item.detail}</span>
                {item.why && <span className="cr-item-why">Reason: {item.why}</span>}
              </div>
            ))}
          </section>
        )}
      </section>

      {/* The negotiation record. */}
      <section className="fd-sec">
        <h3 className="fd-sec-h">Assumptions &amp; Exceptions</h3>
        {written.assumptions.length === 0 && !mayEdit && (
          <p className="cr-said is-empty">None recorded.</p>
        )}
        {written.assumptions.map((row, at) => (
          <div className="cr-assume" key={at}>
            {mayEdit ? (
              <>
                <input
                  className="input"
                  value={row.text}
                  placeholder="What is being taken as true"
                  aria-label={`Assumption ${at + 1}`}
                  onChange={(e) => {
                    const next = [...written.assumptions];
                    next[at] = { ...row, text: e.target.value };
                    setWritten({ ...written, assumptions: next });
                  }}
                  onBlur={() => void keep(written)}
                />
                <select
                  className="input"
                  value={row.decision}
                  aria-label={`Business decision for assumption ${at + 1}`}
                  onChange={(e) => {
                    const next = [...written.assumptions];
                    next[at] = { ...row, decision: e.target.value };
                    void keep({ ...written, assumptions: next });
                  }}
                >
                  {template.decisions.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
                <button
                  className="link cr-drop"
                  aria-label={`Remove assumption ${at + 1}`}
                  onClick={() =>
                    void keep({
                      ...written,
                      assumptions: written.assumptions.filter((_, i) => i !== at),
                    })
                  }
                >
                  Remove
                </button>
              </>
            ) : (
              <>
                <span className="cr-assume-t">{row.text}</span>
                <span className="cr-assume-d">{row.decision}</span>
                {/* DERIVE-FIRST SEED (C) — a settled business decision on an
                    assumption is promoted to the log with no retyping, linked
                    back to this CR (mock 14). A row still "to confirm" shows a
                    muted dash. A quiet secondary that never competes with the
                    document's own primary; writer-gated, RLS is the backstop. */}
                {isSettledDecision(row.decision) ? (
                  canSeed && (
                    <LogDecisionAction
                      label="Log as a decision"
                      note={`Links to ${doc.title}`}
                      draft={decisionFromChangeRequest({
                        crId: doc.id,
                        crRef: doc.title,
                        // The DECISION is the assumption's substance (its title);
                        // the status enum ("Confirmed"/"Deferred") is provenance,
                        // carried into the detail — not the title, which would
                        // otherwise read as the meaningless word "Confirmed".
                        decisionText: row.text,
                        assumptionText: row.decision,
                      })}
                    />
                  )
                ) : (
                  <span style={{ marginLeft: 'auto', color: 'var(--t3)' }} aria-hidden="true">
                    —
                  </span>
                )}
              </>
            )}
          </div>
        ))}
        {mayEdit && (
          <button
            className="link"
            onClick={() =>
              setWritten({
                ...written,
                assumptions: [
                  ...written.assumptions,
                  { text: '', decision: template.decisions[0] } as Assumption,
                ],
              })
            }
          >
            Add an assumption
          </button>
        )}
      </section>

      {/*
        * PRODUCING THE FILE IS WHAT MAKES IT SENDABLE, and the reason it cannot
        * be is said in the same place as the button rather than left to the
        * database to refuse. Same rule as the release gate: a control that
        * exists to be refused teaches people to stop reading refusals.
        */}
      {/* NOT WHEN IT WAS REJECTED. A refusal means there is no approved copy to
          download — the way forward is to supersede it with a version that
          answers the refusal, not to save the one that was turned down (Vinnie,
          30 Sep 2026). The rejection banner above carries that instruction. */}
      {anyoneSigned && doc.filePath && !isRejected(doc, signoffs) && (
        <div className="doc-act cr-produce">
          <button className="btn btn-small" disabled={making} onClick={() => void download()}>
            {making ? 'Preparing…' : 'Download the approved copy'}
          </button>
          <span className="doc-hint">
            The document exactly as it was signed, with a page recording who approved it and when.
          </span>
        </div>
      )}

      {mayEdit && (
        <div className="doc-act cr-produce">
          <button className="btn btn-primary btn-small" disabled={making || !!notReady} onClick={() => void produce()}>
            {making ? 'Producing…' : doc.filePath ? 'Produce it again' : 'Produce the PDF'}
          </button>
          <span className="doc-hint">
            {notReady ||
              (doc.filePath
                ? 'Replaces the file attached to this document.'
                : 'Makes the file that gets signed. Nothing can be sent for signature without it.')}
          </span>
        </div>
      )}
    </div>
  );
}
